import type { Dispatch } from 'redux'
import type { ActionsNetwork } from 'shared/store/actions'
import { actionNetworkError, fetchPageData } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import type { RootState } from 'shared/store/reducers'
import { ws } from 'shared/store/ws'
import type { Coupon } from 'shared/types'
import { ecommerceType, errors } from 'shared/utils'
import { getEcommerceAxiosClient } from 'shared/utils/ecommerce/utils'
import { getCoordinatesFromWoosmap } from 'shared/utils/getGeocoding'
import type { State } from './reducer'
import type { RestaurantCouponsParams, RestaurantSubscribeForm } from './types'

const { fetch, coupons, wallet } = endpoints.pages.restaurant
const { DELIVERY_IN_APP } = ecommerceType

type GetState = () => RootState

type ActionRestaurantPageDataResponse = {
    type: 'PAGE_DATA_RESTAURANT_RESPONSE',
    data: State
}

type ActionPageRestaurantCoupons = {
    type: 'PAGE_RESTAURANT_COUPONS',
    coupons: Array<Coupon>
}

type ActionPageRestaurantSendMailSuccess = {
    type: 'PAGE_RESTAURANT_SEND_MAIL_SUCCESS'
}

type ActionPageRestaurantSendMailError = {
    type: 'PAGE_RESTAURANT_SEND_MAIL_ERROR',
    sendMailError?: string
}

type ActionRestaurantError = {
    type: 'RESTAURANT_ERROR',
    error?: any
}

type ActionRestaurantCouponsError = {
    type: 'RESTAURANT_COUPONS_ERROR',
    couponsError?: string
}

type ActionRestaurantReset = {
    type: 'RESTAURANT_RESET'
}

const fetchRestaurantPageData = (restaurantPath: string) =>
    fetchPageData(
        `${fetch}/${restaurantPath}/page`,
        'PAGE_DATA_RESTAURANT_RESPONSE'
    )

const fetchRestaurantData = (
    pageUrl: string,
    updatedEcommerceMode?: string
) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const {
                ecommerceMode,
                deliveryAddress
            } = getState().ecommerceOrderState
            const { token } = getState().userState
            const currentEcommerceMode = !!updatedEcommerceMode
                ? updatedEcommerceMode
                : ecommerceMode
            const client = getEcommerceAxiosClient(currentEcommerceMode)
            let response
            if (currentEcommerceMode === DELIVERY_IN_APP && deliveryAddress) {
                let paramUrl = ''
                const coordinates = await getCoordinatesFromWoosmap(
                    deliveryAddress
                )
                if (!!coordinates) {
                    paramUrl = `&lat=${coordinates?.lat}&lng=${coordinates?.lng}`
                }
                response = await client.get(
                    `${endpoints.pages.storeLocator}/${pageUrl}?&address=${deliveryAddress}${paramUrl}`,
                    {
                        headers: token
                            ? { RenewToken: false, Authorization: token }
                            : {}
                    }
                )
            } else {
                response = await client.get(`${fetch}/${pageUrl}`)
            }
            return response && response.data
        } catch (error) {
            console.error('Error fetching restaurant data', error)
            return null
        }
    }
}

const fetchCoupons = (r: RestaurantCouponsParams) => async (
    dispatch: Dispatch<any>
) => {
    try {
        actionError(undefined)
        const { restaurantId } = r
        const response = await ws.get(`${coupons}/${restaurantId}`)
        response && dispatch(actionRestaurantCoupons(response.data))
    } catch (error) {
        console.error('Error getting coupons for this restaurant: ', error)
        const networkError = manageNetworkError(error, dispatch)
        if (networkError) {
            return
        }
        const message = manageError(error)
        dispatch(actionCouponsError(message))
    }
}

const actionRestaurantCoupons = (
    coupons: Array<Coupon>
): ActionPageRestaurantCoupons => ({
    type: 'PAGE_RESTAURANT_COUPONS',
    coupons
})

const actionError = (error?: any): ActionRestaurantError => ({
    type: 'RESTAURANT_ERROR',
    error
})

const actionCouponsError = (
    couponsError?: string
): ActionRestaurantCouponsError => ({
    type: 'RESTAURANT_COUPONS_ERROR',
    couponsError
})

const sendFormSuccess = (): ActionPageRestaurantSendMailSuccess => ({
    type: 'PAGE_RESTAURANT_SEND_MAIL_SUCCESS'
})

const sendFormError = (
    sendMailError?: string
): ActionPageRestaurantSendMailError => ({
    type: 'PAGE_RESTAURANT_SEND_MAIL_ERROR',
    sendMailError
})

const reset = (): ActionRestaurantReset => ({
    type: 'RESTAURANT_RESET'
})

const sendMail = (form: RestaurantSubscribeForm) => async (
    dispatch: Dispatch<any>
) => {
    try {
        const { restaurantId, ...otherProps } = form
        dispatch(sendFormError(undefined))
        const response = await ws.post(`${coupons}/${restaurantId}`, otherProps)
        response && dispatch(sendFormSuccess())
    } catch (error) {
        console.error(
            'Error sending form for getting coupons by email: ',
            error
        )
        const networkError = manageNetworkError(error, dispatch)
        if (networkError) {
            return
        }
        const message = manageError(error)
        dispatch(sendFormError(message))
    }
}

const getWalletOfferRestaurant = (
    operationRepeatCode: string,
    restaurantCode: string,
    king: string
) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            dispatch(actionError(undefined))
            const response = await ws.get(
                `${wallet}/${operationRepeatCode}/wallet`,
                {
                    params: {
                        king,
                        restaurantCode
                    }
                }
            )
            return response.data && response.data.wallet
        } catch (error) {
            console.error('Error, could not get coupon for wallet')
            return false
        }
    }
}

const isRestaurantAvailable = (restaurantId: string) => {
    return async () => {
        try {
            const response = await ws.get(`${fetch}/${restaurantId}/quota`, {
                params: { frNumber: restaurantId }
            })
            return response.data
        } catch (error) {
            console.error('Error, could not get restaurant availability')
            return false
        }
    }
}

const manageNetworkError = (error: any, dispatch: Dispatch<any>) => {
    let networkError = true
    switch (error) {
        case errors.NETWORK:
            dispatch(
                actionNetworkError({
                    type: errors.NETWORK
                })
            )
            break
        case errors.TIMEOUT:
            dispatch(
                actionNetworkError({
                    type: errors.TIMEOUT
                })
            )
            break
        default:
            networkError = false
            break
    }
    return networkError
}

const manageError = (error: any) => {
    const { response } = error
    if (response) {
        let message = 'form.error.default'
        const { data } = response
        if (data) {
            switch (data.message) {
                case errors.RESTAURANT_NOT_FOUND:
                    message = 'restaurant.form.not.found'
                    break
                case errors.NOT_ALLOWED:
                case errors.INVALID_CREDENTIALS:
                    message = 'restaurant.offfers.not.allowed'
                    break
                default:
                    break
            }
        }
        return message
    }
    return undefined
}
export {
    actionError,
    fetchCoupons,
    fetchRestaurantPageData,
    fetchRestaurantData,
    getWalletOfferRestaurant,
    isRestaurantAvailable,
    reset,
    sendMail
}

export type Action =
    | ActionRestaurantPageDataResponse
    | ActionPageRestaurantCoupons
    | ActionPageRestaurantSendMailSuccess
    | ActionPageRestaurantSendMailError
    | ActionRestaurantError
    | ActionRestaurantCouponsError
    | ActionRestaurantReset
    | ActionsNetwork
